/**
 * editor2/emailRules.ts
 *
 * メール本文の AI 生成・AI 改善時に Gemini へ与える「基本ルール」。
 * 店舗情報・文章スタイル・キャンセルポリシー等、ドメイン知識を一元化する。
 *
 * 運用ルール:
 *  - お客様への案内文に必ず登場するルール（料金・キャンセルポリシー等）はここに追記
 *  - 文章スタイルや禁止事項もここで集中管理
 *  - 個別テンプレートの内容（決済御礼・リクエスト等）は usePaymentEmail.ts に置く
 */

import { getStoreKey } from '../../config/backend';
import { getInstanceConfig } from '../../config/instances';
import { getStoreBrand } from '../../config/storeBrand';
import { getStorePolicy } from '../../config/storePolicies';

/** 店舗情報ブロック（2026-10-03: 那覇固定だったため宮古の AI メールに那覇の電話・LINE が入っていた） */
function storeInfoBlock(): string {
  const b = getInstanceConfig().branding;
  const brand = getStoreBrand();
  return `## 1. 店舗基本情報（このメールを送る店舗。他店舗の情報を書かない）
- 店舗名: ${b.storeName} / ${b.storeNameEn}（件名のブランド表記は「${brand.tagJa}」/「${brand.tagEn}」）
- 住所: 〒${b.postalAddress}
- 電話: ${b.tel}（受付 ${b.telHours}、海外からは ${b.telIntl}）
${b.lineUrl ? `- LINE（緊急対応）: ${b.lineUrl}\n` : ''}- WEBサイト: ${b.website}
- 運営: ${b.operatorLine.replace(/^Operated by /, '')}
${getStoreKey() === 'pit' ? '- 宮古空港店には深夜・早朝料金はない。那覇空港店の料金・規定・連絡先を書かない\n' : ''}
`;
}

/** キャンセルポリシー（店舗の設定 src/config/storePolicies.ts から。2026-10-06〜 店舗ごとに持つ） */
function cancelPolicyBlock(): string {
  const rows = getStorePolicy().cancellationPolicy.map((r) => `${r.when}：${r.fee}`);
  return ['## 5. キャンセルポリシー', 'お客様にキャンセル料を案内する場合は以下の規定を遵守する:', '',
    '-----------------------------------------------------------', '　　キャンセルポリシー',
    '-----------------------------------------------------------', ...rows, ''].join('\n');
}

/** 呼ぶたびに店舗を反映する（モジュール読み込み時に固定しない） */
export function getEmailBaseRules(): string {
  return EMAIL_BASE_RULES_TEMPLATE
    .replace('{{STORE_INFO}}', storeInfoBlock())
    .replace('{{CANCEL_POLICY}}', cancelPolicyBlock());
}

const EMAIL_BASE_RULES_TEMPLATE = `# メール作成基本ルール

{{STORE_INFO}}## 2. 文章スタイル
- 丁寧かつ簡潔に書く。過度な敬語の重ね使い・定型句の濫用は避ける
- 誤字脱字を絶対に出さない
- 各言語のネイティブが読んで自然な表現にする
- 結びの言葉は言語ごとに自然なものを選ぶ（日: よろしくお願いいたします / 英: Thank you / 繁: 謝謝 / 韓: 감사합니다 など）
- 必ず本文の意味・情報・固有値（予約コード・金額・日時）を正確に保持する

## 3. メール構成（共通）
全メールは以下の3ブロック構成にする:

1. **ヘッダー**: 顧客名 ＋ 予約コード（言語別ラベル）+ 空行
2. **本文**: 用件
3. **フッター**: 区切り線（--------------------）+ 店舗情報ブロック + 区切り線

## 4. 言語別ラベル（予約コード）
- 日本語: 「予約コード」
- 英語: 「Reservation Code」
- 繁體中文: 「預約編號」
- 한국어: 「예약 코드」

{{CANCEL_POLICY}}
## 6. 注意事項
- 料金・日時・予約コードはお客様の予約データから正確に転記する
- データに無い具体的な金額を推測で書かない（不明な場合はプレースホルダや空欄にする）
- 出発前の挨拶・ご来島の言葉は、状況（事前・直前・当日）に応じて選ぶ
- 個人情報（電話・メール・住所）を本文中で再掲する必要はない
- 「お問い合わせはこちら」のような URL を勝手に作らない（記載があるのはフッターの公式 LINE/WEB のみ）
`;
